"use client";

import { useEffect, useRef } from "react";

declare global {
  interface Window {
    Cookiebot?: {
      consent?: {
        necessary?: boolean;
        preferences?: boolean;
        statistics?: boolean;
        marketing?: boolean;
      };
    };
  }
}

export const COOKIEBOT_CONSENT_EVENTS = [
  "CookiebotOnConsentReady",
  "CookiebotOnAccept",
  "CookiebotOnDecline",
  "CookiebotOnChangeSelection",
] as const;

/**
 * Reads the current Cookiebot "statistics" consent.
 * Defaults to `false` until Cookiebot has loaded and the user has chosen,
 * so analytics stays off unless consent is explicitly granted.
 */
export function isStatisticsConsentGranted(): boolean {
  if (typeof window === "undefined") return false;
  return window.Cookiebot?.consent?.statistics === true;
}

/**
 * Runs `onChange` with the current statistics consent on mount and on every
 * Cookiebot consent change.
 */
export function useStatisticsConsent(onChange: (granted: boolean) => void) {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    const sync = () => onChangeRef.current(isStatisticsConsentGranted());
    sync();

    COOKIEBOT_CONSENT_EVENTS.forEach((event) =>
      window.addEventListener(event, sync),
    );
    return () => {
      COOKIEBOT_CONSENT_EVENTS.forEach((event) =>
        window.removeEventListener(event, sync),
      );
    };
  }, []);
}
